import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { onboardingAPI } from '../api';
import Welcome from './onboarding/Welcome';
import ClientInfoFormComplete from './onboarding/ClientInfoFormComplete';
import DocumentUpload from './onboarding/DocumentUpload';
import AgreementReview from './onboarding/AgreementReview';
import SignatureCapture from './onboarding/SignatureCapture';
import Completion from './onboarding/Completion';
import '../App.css';

const STEPS = [
  { id: 1, name: 'Welcome', component: Welcome },
  { id: 2, name: 'Information', component: ClientInfoFormComplete },
  { id: 3, name: 'Documents', component: DocumentUpload },
  { id: 4, name: 'Review', component: AgreementReview },
  { id: 5, name: 'Sign', component: SignatureCapture },
  { id: 6, name: 'Complete', component: Completion }
];

function OnboardingFlow() {
  const { token } = useParams();
  const [session, setSession] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [versionHash, setVersionHash] = useState('');
  const [signatureData, setSignatureData] = useState(null);

  useEffect(() => {
    loadSession();
  }, [token]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentStep]);

  const loadSession = async () => {
    try {
      const response = await onboardingAPI.getSession(token);
      setSession(response.data);
      setFormData(response.data.data || {});
      
      // Determine current step based on session status
      if (response.data.status === 'completed') {
        setCurrentStep(6);
      } else if (response.data.status === 'signed') {
        setCurrentStep(5);
      } else if (response.data.progress >= 80) {
        setCurrentStep(4);
      } else if (response.data.progress >= 60) {
        setCurrentStep(3);
      } else if (response.data.progress > 0) {
        setCurrentStep(2);
      }
      
      setError('');
    } catch (err) {
      if (err.response?.status === 410) {
        setError('This onboarding link has expired. Please contact ST Books for a new link.');
      } else if (err.response?.status === 404) {
        setError('Invalid onboarding link. Please check the URL or contact ST Books.');
      } else {
        setError('Failed to load onboarding session. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const saveProgress = async (data, progress, status = 'started') => {
    try {
      await onboardingAPI.saveData(token, data, progress, status);
    } catch (err) {
      console.error('Failed to auto-save:', err);
    }
  };

  const handleNext = async (stepData) => {
    const updatedData = { ...formData, ...stepData };
    setFormData(updatedData);
    
    const progress = Math.round((currentStep / STEPS.length) * 100);
    await saveProgress(updatedData, progress);
    
    if (currentStep < STEPS.length) {
      setCurrentStep(currentStep + 1);
      window.scrollTo(0, 0);
    }
  };

  const handleBack = () => {
    if (currentStep > 1 && currentStep < 6) {
      setCurrentStep(currentStep - 1);
      window.scrollTo(0, 0);
    }
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>Loading onboarding session...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="loading-screen">
        <div className="alert alert-error" style={{ maxWidth: '500px' }}>
          {error}
        </div>
      </div>
    );
  }

  const CurrentStepComponent = STEPS[currentStep - 1].component;

  return (
    <div style={{ minHeight: '100vh', backgroundColor: 'var(--bg-gray-50)' }}>
      {/* Header */}
      <header style={{
        backgroundColor: 'var(--primary-navy)',
        color: 'var(--text-white)',
        padding: '1.25rem 2rem',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
            {/* Logo */}
            <img src="/logo.webp" alt="ST Books" style={{ width: '96px', height: '96px', borderRadius: '50%' }} />
            <h1 style={{ color: '#F9D0E1', fontSize: '1.75rem', marginBottom: '0' }}>ST Books Client Onboarding</h1>
          </div>
          
          {/* Progress Steps */}
          {currentStep < 6 && (
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              {STEPS.slice(0, 5).map((step) => (
                <div 
                  key={step.id}
                  style={{
                    flex: '1',
                    minWidth: '100px',
                    textAlign: 'center',
                    opacity: currentStep >= step.id ? 1 : 0.5
                  }}
                >
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    backgroundColor: currentStep >= step.id ? '#F9D0E1' : 'rgba(255,255,255,0.2)',
                    color: currentStep >= step.id ? '#003B46' : 'var(--text-muted)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 0.5rem',
                    fontWeight: 'bold',
                    fontSize: '0.875rem'
                  }}>
                    {currentStep > step.id ? '✓' : step.id}
                  </div>
                  <div style={{ fontSize: '0.875rem', color: 'var(--text-white)' }}>
                    {step.name}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main style={{ maxWidth: '900px', margin: '2rem auto', padding: '0 1rem' }}>
        <CurrentStepComponent
          token={token}
          formData={formData}
          onNext={handleNext}
          onBack={handleBack}
          currentStep={currentStep}
          session={session}
          packageType={session?.package}
          packagePrice={session?.price}
          oneOffItems={session?.one_off_items || []}
          versionHash={versionHash}
          setVersionHash={setVersionHash}
          signatureData={signatureData}
          setSignatureData={setSignatureData}
        />
      </main>
    </div>
  );
}

export default OnboardingFlow;